import { Button, Typography } from "@mui/material";
import type { ButtonProps } from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  NO_ACCESS,
  NOT_FOUND,
  type ResourceGetErrorStatus,
  SERVER_ERROR,
} from "@store/api/types";
import React from "react";
import { Link } from "react-router-dom";

import { getAccessRequestUrl } from "@helpers/constants";

const Panel = styled("div")(({ theme }) => ({
  display: "grid",
  gap: theme.spacing(2),
  justifyItems: "start",
}));

const Title = styled(Typography)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
}));

const Actions = styled("div")(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(2),
}));

const MESSAGES: Record<
  ResourceGetErrorStatus,
  (resourceLabel: string) => string
> = {
  [NO_ACCESS]: (label) => `You don't have permission to view this ${label}`,
  [NOT_FOUND]: (label) => `This ${label} does not appear to exist`,
  [SERVER_ERROR]: () =>
    "Something unexpected occurred. Please contact support for help.",
};

function messageFor(
  status: ResourceGetErrorStatus,
  resourceLabel: string,
): string {
  return MESSAGES[status](resourceLabel);
}

export interface ResourceAccessErrorPanelProps {
  status: ResourceGetErrorStatus;
  /** Label used to build the default messages, e.g. "job" or "data" */
  resourceLabel: string;
  /** Resource id to prefill on the "Request access" link, if the resource is known */
  resourceId?: string;
  backTo: string;
  backLabel: string;
  backButtonVariant?: ButtonProps["variant"];
  titleIcon?: React.ReactNode;
}

export function ResourceAccessErrorPanel({
  status,
  resourceLabel,
  resourceId,
  backTo,
  backLabel,
  backButtonVariant,
  titleIcon,
}: ResourceAccessErrorPanelProps) {
  return (
    <Panel>
      <Title variant="h3">
        {titleIcon}
        Sorry
      </Title>
      <Typography variant="body1" color="textSecondary">
        {messageFor(status, resourceLabel)}
      </Typography>
      <Actions>
        <Button
          color="primary"
          variant={backButtonVariant}
          component={Link}
          to={backTo}
        >
          {backLabel}
        </Button>
        {status === NO_ACCESS && (
          <Button
            color="primary"
            variant="contained"
            component={Link}
            to={getAccessRequestUrl(resourceId)}
          >
            Request access
          </Button>
        )}
      </Actions>
    </Panel>
  );
}

export default ResourceAccessErrorPanel;
